import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import {
    FaMoneyBillWave,
    FaExchangeAlt,
    FaCreditCard,
    FaGift,
    FaBriefcase,
    FaHandHoldingUsd,
    FaCopy,
    FaCheck,
    FaTimes
} from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';

const TransactionHistory = () => {
    const [typeFilter, setTypeFilter] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [lastId, setLastId] = useState(0);
    const [loading, setLoading] = useState(true); // Start with true to show loading state
    const [hasMore, setHasMore] = useState(true);
    const [copiedId, setCopiedId] = useState(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const navigate = useNavigate();
    const containerRef = useRef(null);

    const typeOptions = [
        { value: '', label: 'All Transactions' },
        { value: 'trade', label: 'Trade' },
        { value: 'trade profit', label: 'Trade Profit' },
        { value: 'USDT to coin swap', label: 'USDT to Coin Swap' },
        { value: 'coin to USDT swap', label: 'Coin to USDT Swap' },
        { value: 'commission', label: 'Commission' },
        { value: 'coupon', label: 'Coupon Claim' },
        { value: 'bonus', label: 'Bonus' },
        { value: 'salary', label: 'Salary' },
    ];

    const getTransactionIcon = (type) => {
        switch (type) {
            case 'deposit': return <FaMoneyBillWave />;
            case 'withdrawal': return <FaCreditCard />;
            case 'trade': return <FaExchangeAlt />;
            case 'bonus': return <FaGift />;
            case 'salary': return <FaBriefcase />;
            case 'commission': return <FaHandHoldingUsd />;
            default: return <FaMoneyBillWave />;
        }
    };

    const getTransactionColor = (type) => {
        switch (type) {
            case 'deposit': return 'success';
            case 'withdrawal': return 'warning';
            case 'trade': return 'primary';
            case 'bonus': return 'info';
            case 'salary': return 'success';
            case 'commission': return 'info';
            default: return 'primary';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };

    const formatAmount = (amount, type) => {
        const absAmount = Math.abs(amount);
        let formatted;

        if (absAmount % 1 === 0) {
            formatted = absAmount.toLocaleString('en-US');
        } else {
            const decimalPart = absAmount.toString().split('.')[1];
            const decimalPlaces = Math.min(decimalPart ? decimalPart.length : 0, 5);
            formatted = absAmount.toLocaleString('en-US', {
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces
            });
        }

        // FIXED: Use the type parameter (0 for debit, 1 for credit) instead of amount value
        const sign = type == 1 ? '+' : '-';
        const amount_text = `${sign}${formatted}`;
        const amount_color = type == 1 ? "success" : "danger";

        return { amount_text, amount_color };
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(text);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    const resetDateFilter = () => {
        setSelectedDate(null);
        loadMoreTransactions(true, true, typeFilter, 0, null);
    };

    // Load initial transactions
    useEffect(() => {
        loadMoreTransactions(true, true, '', 0, null);
    }, []);

    const loadMoreTransactions = async (reset = false, has_more = hasMore, payment_type = typeFilter, last_id = lastId, date = selectedDate) => {
        if (!has_more) return;
        if (loading && !reset) return; // Allow loading when resetting

        setLoading(true);

        const formated_date = date ? moment(date).format('YYYY-MM-DD') : '';

        try {
            const response = await axios.post(`${BASE_API_URL}/payment/transaction-report`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                last_id: last_id,
                payment_type: payment_type,
                date: formated_date
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error == 'session expired') {
                    localStorage.clear('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }
            if (data?.data) {
                if (reset) {
                    setTransactions(data?.data || []);
                } else {
                    setTransactions(prev => [...prev, ...(data?.data || [])]);
                }

                setHasMore(data?.has_more || false);
            }
            if (data?.last_id) {
                setLastId(data?.last_id);
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to load transactions');
        } finally {
            setLoading(false);
            if (!initialLoadComplete) {
                setInitialLoadComplete(true);
            }
        }
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading && initialLoadComplete) {
            loadMoreTransactions(false, hasMore, typeFilter, lastId, selectedDate);
        }
    };

    const getSelectedTypeLabel = () => {
        const selected = typeOptions.find(option => option.value === typeFilter);
        return selected ? selected.label : 'All Transactions';
    };

    // Custom input component for DatePicker without calendar icon
    const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
        <input
            type="text"
            className="form-control form-control-sm"
            value={value}
            onClick={onClick}
            ref={ref}
            readOnly
            placeholder="Select date"
        />
    ));

    // Animation variants for smooth transitions
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.3,
                when: "beforeChildren",
                staggerChildren: 0.05
            }
        }
    };

    const itemVariants = {
        hidden: {
            opacity: 0,
            y: 10
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    };

    const loadingVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.3 }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="p-3"
            style={{ marginTop: '-1rem' }}
        >
            {/* Header with icon and reduced margin */}
            <motion.div
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="mb-2"
            >
                <div className="d-flex align-items-center mb-1">
                    <span
                        className="fs-4 me-2 p-2 rounded-circle d-inline-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: 'var(--bs-primary-bg-subtle)',
                            width: '40px',
                            height: '40px'
                        }}
                    >
                        📜
                    </span>
                    <h1 className="h4 fw-bold mb-0">Transaction History</h1>
                </div>
                <p className="text-muted small mb-2">Track all your financial activities</p>
            </motion.div>

            {/* Filter Dropdowns */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05, duration: 0.3 }}
                className="row g-2 mb-3"
            >
                <div className="col-md-6">
                    <label className="form-label small fw-semibold">Transaction Type</label>
                    <select
                        className="form-select form-select-sm"
                        value={typeFilter}
                        onChange={(e) => {
                            setTypeFilter(e.target.value);
                            setLastId(0);
                            setHasMore(true);
                            loadMoreTransactions(true, true, e.target.value, 0, selectedDate);
                        }}
                    >
                        {typeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-6">
                    <label className="form-label small fw-semibold">Date</label>
                    <div className="d-flex gap-2">
                        <div className="flex-grow-1">
                            <DatePicker
                                selected={selectedDate}
                                dateFormat="dd/MM/YYYY"
                                onChange={(date) => {
                                    setSelectedDate(date);
                                    loadMoreTransactions(true, true, typeFilter, 0, date);
                                }}
                                customInput={<CustomInput />}
                                isClearable={false}
                                placeholderText="Select date"
                            />
                        </div>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={resetDateFilter}
                            title="Reset date filter"
                            style={{ minWidth: '40px' }}
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* Transaction List */}
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="card border-0 shadow-sm"
                style={{
                    maxHeight: '70vh',
                    overflow: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
                onScroll={handleScroll}
                ref={containerRef}
            >
                <style>
                    {`
                        .card::-webkit-scrollbar {
                            display: none;
                        }
                        .react-datepicker-wrapper {
                            width: 100%;
                        }
                    `}
                </style>

                <div className="card-header bg-transparent border-0 py-2">
                    <div className="row align-items-center">
                        <div className="col">
                            <h6 className="mb-0 fw-bold">
                                {getSelectedTypeLabel()}
                            </h6>
                        </div>
                    </div>
                </div>

                <div className="card-body p-0">
                    <AnimatePresence mode="sync">
                        {loading && !initialLoadComplete ? (
                            <motion.div
                                key="loading-state"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="text-center py-4"
                                style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                            >
                                <div className="spinner-border text-primary mb-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted small">Loading transactions...</p>
                            </motion.div>
                        ) : transactions.length === 0 ? (
                            <motion.div
                                key="empty-state"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="text-center py-4"
                                style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                            >
                                <div className="fs-1 mb-2">📭</div>
                                <h6 className="text-muted">No transactions found</h6>
                                <p className="text-muted small">Try adjusting your filters</p>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="transaction-list"
                                initial="hidden"
                                animate="visible"
                                variants={containerVariants}
                            >
                                {transactions.map((transaction, index) => {
                                    const formattedTimestamp = formatDate(transaction.date);
                                    const color = getTransactionColor(transaction.payment_type);
                                    const icon = getTransactionIcon(transaction.payment_type);
                                    // FIXED: Pass transaction.type to formatAmount to determine debit/credit
                                    const { amount_text, amount_color } = formatAmount(transaction.amount, transaction.type);

                                    return (
                                        <motion.div
                                            key={transaction.id || index}
                                            variants={itemVariants}
                                            layout
                                            className="border-bottom"
                                            style={{ borderColor: 'rgba(0,0,0,0.1)' }}
                                            whileHover={{
                                                backgroundColor: 'rgba(0,0,0,0.02)',
                                                transition: { duration: 0.1 }
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-between p-2">
                                                <div className="d-flex align-items-center flex-grow-1">
                                                    <motion.span
                                                        className="fs-6 me-2 p-2 rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            backgroundColor: `var(--bs-${color}-bg-subtle)`,
                                                            color: `var(--bs-${color})`,
                                                            flexShrink: 0
                                                        }}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        {icon}
                                                    </motion.span>
                                                    <div className="flex-grow-1 min-width-0">
                                                        <div className="fw-semibold small text-truncate text-capitalize">
                                                            {transaction.payment_type}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                                                            <small className="text-muted font-monospace text-truncate">
                                                                {transaction.id}
                                                            </small>
                                                            <motion.button
                                                                className="btn btn-sm p-0 border-0 flex-shrink-0"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    copyToClipboard(transaction.id);
                                                                    toast.success('Transaction id copied')
                                                                }}
                                                                title="Copy Transaction ID"
                                                                whileHover={{ scale: 1.1 }}
                                                                whileTap={{ scale: 0.9 }}
                                                            >
                                                                {copiedId === transaction.id ? (
                                                                    <FaCheck className="text-success" size={12} />
                                                                ) : (
                                                                    <FaCopy className="text-muted" size={12} />
                                                                )}
                                                            </motion.button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-end flex-shrink-0 ms-2" style={{ minWidth: '120px' }}>
                                                    <motion.div
                                                        className={`fw-bold text-${amount_color}`}
                                                        style={{
                                                            fontSize: amount_text.length > 12 ? '0.8rem' : '0.875rem'
                                                        }}
                                                        whileHover={{ scale: 1.02 }}
                                                    >
                                                        {amount_text}
                                                    </motion.div>
                                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {formattedTimestamp}
                                                    </small>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}

                                {/* Loading Indicator for pagination */}
                                {loading && initialLoadComplete && (
                                    <motion.div
                                        key="loading-indicator"
                                        variants={loadingVariants}
                                        initial="hidden"
                                        animate="visible"
                                        className="text-center py-3"
                                    >
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <small className="text-muted ms-2">Loading more transactions...</small>
                                    </motion.div>
                                )}

                                {/* No More Data Indicator */}
                                {!hasMore && transactions.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-center py-3 border-top"
                                    >
                                        <small className="text-muted">No more transactions to load</small>
                                    </motion.div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default TransactionHistory;